import { type FC, useState } from 'react';

import { Dropdown, type DropdownProps, GenericMenuList } from '@craft/ui/components/lists';
import { Divider } from '@craft/ui/components/structural';

import { useUser } from '@/src/features/auth/hooks';
import { useApplication } from '@/src/features/config/hooks';
import { constrainWidthToRightViewportEdge } from '@/src/features/content-tagging/components/dropdowns/CompetencyTaggingDropdown';

import { UserProfileButton } from '../../buttons/UserProfileButton';

import { UserProfileMenuItemType } from './types';
import { UserProfileMenuItem } from './UserProfileMenuItem';

export interface UserProfileDropdownProps extends Omit<
  DropdownProps,
  'children' | 'content' | 'offset' | 'placement'
> {}

export const UserProfileDropdown: FC<UserProfileDropdownProps> = props => {
  const { logout } = useApplication();
  const user = useUser();

  /**
   * State that tracks whether or not the logout action is in the process of being performed via
   * a hard navigation to the logout URL.
   *
   * This state is not intended to be cleared or set to false after the action occurs because the
   * action is not asynchronous and the result of the navigation results in a full page reload on a
   * different URL - so clearing the state after the navigation is not applicable.
   */
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  return (
    <Dropdown
      {...props}
      content={({ isOpen, props: listProps, withClose }) => (
        <GenericMenuList {...listProps} aria-label='profile menu' autoFocusItem={isOpen}>
          <UserProfileMenuItem
            onClick={withClose(() => undefined)}
            type={UserProfileMenuItemType.ViewProfile}
          />
          {user.organizationRoles.length > 1 && (
            <UserProfileMenuItem
              onClick={withClose(() => undefined)}
              type={UserProfileMenuItemType.SwitchView}
            />
          )}
          {user.organizationRoles.length > 1 && <Divider sx={{ marginY: 1 }} variant='middle' />}
          <UserProfileMenuItem
            description={user.email}
            isLoading={isLoggingOut}
            onClick={() => {
              setIsLoggingOut(true);
              logout({ navigateCurrentTab: false });
            }}
            type={UserProfileMenuItemType.SignOut}
          />
        </GenericMenuList>
      )}
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 10,
          },
        },
        {
          fn({ state }) {
            return constrainWidthToRightViewportEdge(state);
          },
          name: 'constrainWidthToRightViewportEdge',
          phase: 'main',
        },
      ]}
      offset={[0, 8]}
      placement='bottom-end'
      shouldCloseOnTargetClick
    >
      {({ isOpen, props: targetProps, ref }) => (
        <UserProfileButton {...targetProps} isOpen={isOpen} ref={ref} sx={{ maxWidth: '100%' }} />
      )}
    </Dropdown>
  );
};
